const ODOO_CONFIG = {
    url: 'https://react-ai-demos.xamta.in/api/',
    db: 'e18_rpc_test',
    username: 'admin',
    password: 'xadmin'
};

let uid = null;
const cache = {
    partners: null,
    projects: null,
    lastFetch: {
        partners: 0,
        projects: 0
    }
};
const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

const rpcCall = async (endpoint, params) => {
    // Use relative path so the request goes to the React dev server 
    // and gets proxied to Odoo, bypassing CORS!
    const response = await fetch(endpoint, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json'
        },
        body: JSON.stringify({
            jsonrpc: '2.0',
            method: 'call',
            params: params,
            id: Math.floor(Math.random() * 10000)
        })
    });

    const data = await response.json();
    if (data.error) {
        throw new Error(data.error.data?.message || data.error.message || 'Odoo RPC error');
    }
    return data.result;
};

const ensureLogin = async () => {
    if (uid) return uid;
    const result = await rpcCall('/web/session/authenticate', {
        db: ODOO_CONFIG.db,
        login: ODOO_CONFIG.username,
        password: ODOO_CONFIG.password
    });

    if (result && result.uid) {
        uid = result.uid;
        return uid;
    } else {
        throw new Error('Login failed: Invalid credentials');
    }
};

const searchRead = async (model, domain = [], fields = [], limit = 0, offset = 0, order = '') => {
    await ensureLogin();
    return await rpcCall('/web/dataset/call_kw', {
        model,
        method: 'search_read',
        args: [domain],
        kwargs: { fields, limit, offset, order }
    });
};

const searchCount = async (model, domain = []) => {
    await ensureLogin();
    return await rpcCall('/web/dataset/call_kw', {
        model,
        method: 'search_count',
        args: [domain],
        kwargs: {}
    });
};

/**
 * Fetch project details by ID
 */
export const getProjectDetails = async (id) => {
    try {
        const projects = await searchRead(
            'project.project',
            [['id', '=', parseInt(id)]],
            ['name', 'partner_id', 'description', 'date_start', 'task_count', 'date', 'x_contract_value'],
            1
        );
        return projects.length > 0 ? projects[0] : null;
    } catch (error) {
        console.error("Error fetching project:", error);
        throw error;
    }
};

/**
 * Fetch projects with search/pagination
 */
export const getAllProjects = async (domain = [], limit = 80, offset = 0) => {
    try {
        const [projects, count] = await Promise.all([
            searchRead(
                'project.project',
                domain,
                ['name', 'partner_id', 'description', 'date_start', 'task_count', 'date', 'x_contract_value'],
                limit,
                offset,
                'create_date desc'
            ),
            searchCount('project.project', domain)
        ]);
        return { projects, count };
    } catch (error) {
        console.error("Error fetching projects:", error);
        throw error;
    }
};

/**
 * Fetch payments based on a given domain
 */
export const getPayments = async (domain = [], limit = 80, offset = 0) => {
    try {
        const [payments, count] = await Promise.all([
            searchRead(
                'account.payment',
                domain,
                ['date', 'name', 'partner_id', 'amount_company_currency_signed', 'state'],
                limit,
                offset,
                'date desc'
            ),
            searchCount('account.payment', domain)
        ]);
        return { payments, count };
    } catch (error) {
        console.error("Error fetching payments:", error);
        throw error;
    }
};

/**
 * Fetch tasks for a specific project */
export const getProjectTasks = async (project_id) => {
    try {
        const tasks = await searchRead(
            'project.task',
            [['project_id', '=', parseInt(project_id)]], // Filter by project_id
            ['name', 'date_deadline', 'stage_id', 'description', 'user_ids', 'partner_id', 'tag_ids', 'state'],
            0,
            0,
            'create_date desc'
        );
        return tasks;
    } catch (error) {
        console.error("Error fetching tasks:", error);
        throw error;
    }
};

/**
 * Fetch timesheets
 */
export const getTimesheets = async (domain = [], limit = 80, offset = 0) => {
    try {
        const [timesheets, count] = await Promise.all([
            searchRead(
                'account.analytic.line',
                domain,
                ['name', 'date', 'task_id', 'unit_amount', 'user_id', 'project_id'],
                limit,
                offset,
                'date desc'
            ),
            searchCount('account.analytic.line', domain)
        ]);
        return { timesheets, count };
    } catch (error) {
        console.error("Error fetching timesheets:", error);
        throw error;
    }
};

/**
 * Fetch invoices
 */
export const getInvoices = async (domain = [], limit = 80, offset = 0) => {
    try {
        const [invoices, count] = await Promise.all([
            searchRead(
                'account.move',
                domain,
                ['name', 'partner_id', 'invoice_date', 'invoice_date_due', 'amount_total', 'payment_state', 'status_in_payment', 'access_token'],
                limit,
                offset,
                'invoice_date desc'
            ),
            searchCount('account.move', domain)
        ]);
        return { invoices, count };
    } catch (error) {
        console.error("Error fetching invoices:", error);
        throw error;
    }
};


/* ── Generic write/create helpers ── */

const odooWrite = async (model, id, data) => {
    await ensureLogin();
    return await rpcCall('/web/dataset/call_kw', {
        model,
        method: 'write',
        args: [[parseInt(id)], data],
        kwargs: {}
    });
};

const odooCreate = async (model, data) => {
    await ensureLogin();
    return await rpcCall('/web/dataset/call_kw', {
        model,
        method: 'create',
        args: [data],
        kwargs: {}
    });
};

const odooUnlink = async (model, id) => {
    await ensureLogin();
    return await rpcCall('/web/dataset/call_kw', {
        model,
        method: 'unlink',
        args: [[parseInt(id)]],
        kwargs: {}
    });
};

/* ── Contracts (project.project) ── */
export const createProject = async (data) => {
    try { return await odooCreate('project.project', data); }
    catch (e) { console.error('createProject', e); throw e; }
};
export const updateProject = async (id, data) => {
    try { return await odooWrite('project.project', id, data); }
    catch (e) { console.error('updateProject', e); throw e; }
};

/* ── Timesheets (account.analytic.line) ── */
export const createTimesheet = async (data) => {
    try { return await odooCreate('account.analytic.line', data); }
    catch (e) { console.error('createTimesheet', e); throw e; }
};
export const updateTimesheet = async (id, data) => {
    try { return await odooWrite('account.analytic.line', id, data); }
    catch (e) { console.error('updateTimesheet', e); throw e; }
};

/* ── Payments (account.payment) ── */
export const createPayment = async (data) => {
    try { return await odooCreate('account.payment', data); }
    catch (e) { console.error('createPayment', e); throw e; }
};
export const updatePayment = async (id, data) => {
    try { return await odooWrite('account.payment', id, data); }
    catch (e) { console.error('updatePayment', e); throw e; }
};

/* ── Invoices (account.move) ── */
export const createInvoice = async (data) => {
    try { return await odooCreate('account.move', data); }
    catch (e) { console.error('createInvoice', e); throw e; }
};
export const updateInvoice = async (id, data) => {
    try { return await odooWrite('account.move', id, data); }
    catch (e) { console.error('updateInvoice', e); throw e; }
};

/* ── Tasks (project.task) ── */
export const createTask = async (data) => {
    try { return await odooCreate('project.task', data); }
    catch (e) { console.error('createTask', e); throw e; }
};
export const updateTask = async (id, data) => {
    try { return await odooWrite('project.task', id, data); }
    catch (e) { console.error('updateTask', e); throw e; }
};

export const searchPartners = async (query = '', forceRefresh = false) => {
    const now = Date.now();
    // Cache only if it's a generic search (no query)
    if (!query && !forceRefresh && cache.partners && (now - cache.lastFetch.partners < CACHE_DURATION)) {
        return cache.partners;
    }

    try {
        const domain = [['customer_rank', '>', 0]];
        if (query) {
            domain.push(['name', 'ilike', query]);
        }
        const results = await searchRead('res.partner', domain, ['name', 'display_name'], 100);
        
        if (!query) {
            cache.partners = results;
            cache.lastFetch.partners = now;
        }
        return results;
    } catch (e) {
        console.error('searchPartners', e);
        throw e;
    }
};

/* ── Profile (res.users & res.partner) ── */

export const getUserProfile = async (userId) => {
    try {
        await ensureLogin();
        // 1. Get partner_id from res.users
        const users = await searchRead('res.users', [['id', '=', parseInt(userId)]], ['partner_id'], 1);
        if (users.length === 0) return null;
        
        const partnerId = users[0].partner_id[0];
        
        // 2. Get detailed partner info
        const partners = await searchRead(
            'res.partner', 
            [['id', '=', partnerId]], 
            ['name', 'street', 'street2', 'zip', 'city', 'email', 'country_id', 'state_id', 'phone', 'mobile', 'image_1920'],
            1
        );
        return partners.length > 0 ? partners[0] : null;
    } catch (e) {
        console.error('getUserProfile', e);
        throw e;
    }
};

export const updateUserProfile = async (partnerId, data) => {
    try {
        return await odooWrite('res.partner', partnerId, data);
    } catch (e) {
        console.error('updateUserProfile', e);
        throw e;
    }
};

export const updateUserDetails = async (userId, data) => {
    try {
        return await odooWrite('res.users', userId, data);
    } catch (e) {
        console.error('updateUserDetails', e);
        throw e;
    }
};

export const getCountries = async () => {
    try {
        return await searchRead('res.country', [], ['name', 'code'], 300, 0, 'name asc');
    } catch (e) {
        console.error('getCountries', e);
        throw e;
    }
};

export const getStates = async (countryId) => {
    try {
        const domain = countryId ? [['country_id', '=', parseInt(countryId)]] : [];
        return await searchRead('res.country.state', domain, ['name', 'code', 'country_id'], 1000, 0, 'name asc');
    } catch (e) {
        console.error('getStates', e);
        throw e;
    }
};

export const getTimezones = async () => {
    try {
        await ensureLogin();
        const result = await rpcCall('/web/dataset/call_kw', {
            model: 'res.users',
            method: 'fields_get',
            args: [['tz']],
            kwargs: { attributes: ['selection'] }
        });
        return result.tz.selection;
    } catch (e) {
        console.error('getTimezones', e);
        return [];
    }
};

export const getLanguages = async () => {
    try {
        await ensureLogin();
        const result = await rpcCall('/web/dataset/call_kw', {
            model: 'res.users',
            method: 'fields_get',
            args: [['lang']],
            kwargs: { attributes: ['selection'] }
        });
        return result.lang.selection;
    } catch (e) {
        console.error('getLanguages', e);
        return [];
    }
};

/* ── Attachments (ir.attachment) ── */
export const getTaskAttachments = async (taskId) => {
    try {
        return await searchRead(
            'ir.attachment',
            [['res_model', '=', 'project.task'], ['res_id', '=', parseInt(taskId)]],
            ['name', 'mimetype', 'file_size', 'create_date'],
            0,
            0,
            'create_date desc'
        );
    } catch (e) {
        console.error('getTaskAttachments', e);
        throw e;
    }
};

export const uploadTaskAttachment = async (taskId, fileName, base64Data) => {
    try {
        return await odooCreate('ir.attachment', {
            name: fileName,
            datas: base64Data,
            res_model: 'project.task',
            res_id: parseInt(taskId),
            type: 'binary'
        });
    } catch (e) {
        console.error('uploadTaskAttachment', e);
        throw e;
    }
};

export const deleteAttachment = async (attachmentId) => {
    try {
        return await odooUnlink('ir.attachment', attachmentId);
    } catch (e) {
        console.error('deleteAttachment', e);
        throw e;
    }
};

export const GetUserDetails = async (userId) => {
    try {
        return await searchRead(
            'res.users',
            [['id', '=', parseInt(userId)]],
            ['lang','tz'],
            1
        );
    } catch (e) {
        console.error('getTimesheetAttachments', e);
        throw e;
    }
};

/* ── Attendance (hr.attendance) ── */

/** Fetch attendance records with pagination */
export const getAttendanceRecords = async (domain = [], limit = 80, offset = 0) => {
    try {
        const [records, count] = await Promise.all([
            searchRead(
                'hr.attendance',
                domain,
                [
                    'employee_id', 'check_in', 'check_out',
                    'worked_hours', 'overtime_hours', 'validated_overtime_hours',
                    'in_mode', 'out_mode',
                    'in_ip_address', 'out_ip_address',
                    'in_country_name', 'out_country_name',
                    'in_latitude', 'out_latitude',
                    'in_browser', 'out_browser',
                ],
                limit,
                offset,
                'check_in desc'
            ),
            searchCount('hr.attendance', domain)
        ]);
        return { records, count };
    } catch (e) {
        console.error('getAttendanceRecords', e);
        throw e;
    }
};

/** Find today's open attendance record for an employee (checked-in but not checked-out) */
export const getOpenAttendance = async (employeeId) => {
    try {
        const results = await searchRead(
            'hr.attendance',
            [['employee_id', '=', parseInt(employeeId)], ['check_out', '=', false]],
            ['employee_id', 'check_in', 'worked_hours', 'in_mode'],
            1,
            0,
            'check_in desc'
        );
        return results.length > 0 ? results[0] : null;
    } catch (e) {
        console.error('getOpenAttendance', e);
        return null;
    }
};

/** Get employee linked to a user id.
 *  Strategy 1: match hr.employee.user_id directly
 *  Strategy 2: match hr.employee.name against res.users.name (admin fallback)
 *  Strategy 3: return the first employee found (so admin can always act)
 */
export const getEmployeeByUserId = async (userId) => {
    try {
        // Strategy 1 — direct link
        const direct = await searchRead(
            'hr.employee',
            [['user_id', '=', parseInt(userId)]],
            ['id', 'name'],
            1
        );
        if (direct.length > 0) return direct[0];

        // Strategy 2 — name match via res.users
        const users = await searchRead(
            'res.users',
            [['id', '=', parseInt(userId)]],
            ['name'],
            1
        );
        if (users.length > 0) {
            const userName = users[0].name;
            const byName = await searchRead(
                'hr.employee',
                [['name', 'ilike', userName]],
                ['id', 'name'],
                1
            );
            if (byName.length > 0) return byName[0];
        }

        // Strategy 3 — first available employee (admin viewing all records)
        const any = await searchRead('hr.employee', [], ['id', 'name'], 1, 0, 'id asc');
        return any.length > 0 ? any[0] : null;
    } catch (e) {
        console.error('getEmployeeByUserId', e);
        return null;
    }
};

/** Create a new check-in record */
export const checkIn = async (employeeId) => {
    try {
        const now = new Date();
        // Odoo expects UTC datetime string
        const checkInUtc = now.toISOString().replace('T', ' ').substring(0, 19);
        const newId = await odooCreate('hr.attendance', {
            employee_id: parseInt(employeeId),
            check_in: checkInUtc,
            in_mode: 'technical'
        });
        return newId;
    } catch (e) {
        console.error('checkIn', e);
        throw e;
    }
};

/** Write check_out on an existing attendance record */
export const checkOut = async (attendanceId) => {
    try {
        const now = new Date();
        const checkOutUtc = now.toISOString().replace('T', ' ').substring(0, 19);
        return await odooWrite('hr.attendance', attendanceId, {
            check_out: checkOutUtc,
            out_mode: 'technical'
        });
    } catch (e) {
        console.error('checkOut', e);
        throw e;
    }
};

/** Get total worked hours today for an employee */
export const getTodayWorkedHours = async (employeeId) => {
    try {
        const todayStart = new Date();
        todayStart.setHours(0, 0, 0, 0);
        const todayUtc = todayStart.toISOString().replace('T', ' ').substring(0, 19);
        const records = await searchRead(
            'hr.attendance',
            [['employee_id', '=', parseInt(employeeId)], ['check_in', '>=', todayUtc]],
            ['worked_hours', 'check_in', 'check_out'],
            0, 0, 'check_in asc'
        );
        return records;
    } catch (e) {
        console.error('getTodayWorkedHours', e);
        return [];
    }
};

export default {
    getProjectDetails,
    GetUserDetails,
    getAllProjects,
    createProject,
    updateProject,
    getPayments,
    createPayment,
    updatePayment,
    getProjectTasks,
    createTask,
    updateTask,
    getTimesheets,
    createTimesheet,
    updateTimesheet,
    getInvoices,
    createInvoice,
    updateInvoice,
    searchPartners,
    getUserProfile,
    updateUserProfile,
    updateUserDetails,
    getCountries,
    getStates,
    getTimezones,
    getLanguages,
    getTaskAttachments,
    uploadTaskAttachment,
    deleteAttachment,
    getAttendanceRecords,
    getOpenAttendance,
    getEmployeeByUserId,
    checkIn,
    checkOut,
    getTodayWorkedHours
};
